import React, { useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IllustrativeBanner } from "@/components/Disclaimers";

const MODELS = [
  { id: "percentage_first_month", label: "% of first-month collected revenue" },
  { id: "fixed_placement_fee", label: "Fixed placement fee" },
  { id: "percentage_provider_fee", label: "% of provider placement fee" },
  { id: "fixed_acquisition_fee", label: "Fixed customer-acquisition fee" },
  { id: "percentage_engagement", label: "% of defined service engagement" },
  { id: "percentage_respite_booking", label: "% of respite booking" },
  { id: "flat_business_development_fee", label: "Flat business-development fee" },
];

const SERVICES = ["Assisted Living", "Family Care Homes", "Memory Care", "Independent Living",
  "Private-Duty Home Care", "Personal Care", "Companion Care", "Residential Respite", "In-Home Respite",
  "Adult Day Services", "Care Management", "Senior Transition / Move Management", "Private Transportation"];

const money = (n) => "$" + (Math.round(n) || 0).toLocaleString();

export default function CommissionCalculator({ compact = false }) {
  const [service, setService] = useState("Assisted Living");
  const [model, setModel] = useState("percentage_first_month");
  const [monthlyValue, setMonthlyValue] = useState(10000);
  const [engagementValue, setEngagementValue] = useState(0);
  const [providerFee, setProviderFee] = useState(10000);
  const [placementPct, setPlacementPct] = useState(100);
  const [agentShare, setAgentShare] = useState(50);
  const [fixedCommission, setFixedCommission] = useState(750);

  const result = useMemo(() => {
    let providerReceives = 0;
    let agentCommission = 0;
    if (model === "percentage_first_month") {
      providerReceives = monthlyValue * (placementPct / 100);
      agentCommission = providerReceives * (agentShare / 100);
    } else if (model === "fixed_placement_fee" || model === "fixed_acquisition_fee" || model === "flat_business_development_fee") {
      providerReceives = fixedCommission;
      agentCommission = fixedCommission;
    } else if (model === "percentage_provider_fee") {
      providerReceives = providerFee;
      agentCommission = providerFee * (agentShare / 100);
    } else if (model === "percentage_engagement" || model === "percentage_respite_booking") {
      providerReceives = engagementValue * (placementPct / 100);
      agentCommission = providerReceives * (agentShare / 100);
    }
    const customerValue = model.includes("engagement") || model.includes("respite") ? engagementValue : monthlyValue;
    return { customerValue, providerReceives, agentCommission, providerRetained: Math.max(providerReceives - agentCommission, 0) };
  }, [model, monthlyValue, engagementValue, providerFee, placementPct, agentShare, fixedCommission]);

  const showPct = ["percentage_first_month", "percentage_engagement", "percentage_respite_booking"].includes(model);
  const showFixed = ["fixed_placement_fee", "fixed_acquisition_fee", "flat_business_development_fee"].includes(model);
  const showProviderFee = model === "percentage_provider_fee";
  const showShare = showPct || showProviderFee;
  const showEngagement = ["percentage_engagement", "percentage_respite_booking"].includes(model);

  return (
    <div className={`grid gap-6 ${compact ? "" : "lg:grid-cols-5"}`} data-testid="commission-calculator">
      <Card className="p-6 lg:col-span-3">
        <div className="grid gap-5">
          <div className="grid gap-2 sm:grid-cols-2 sm:gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Service category</Label>
              <Select value={service} onValueChange={setService}>
                <SelectTrigger data-testid="calc-service" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Compensation model</Label>
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger data-testid="calc-model" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{MODELS.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {!showEngagement && !showFixed && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Monthly customer value: {money(monthlyValue)}</Label>
              <Input type="number" data-testid="calc-monthly" className="mt-1" value={monthlyValue}
                     onChange={(e) => setMonthlyValue(Number(e.target.value))} />
            </div>
          )}
          {showEngagement && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">One-time engagement / booking value</Label>
              <Input type="number" data-testid="calc-engagement" className="mt-1" value={engagementValue}
                     onChange={(e) => setEngagementValue(Number(e.target.value))} />
            </div>
          )}
          {showProviderFee && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Provider placement/acquisition fee</Label>
              <Input type="number" data-testid="calc-provider-fee" className="mt-1" value={providerFee}
                     onChange={(e) => setProviderFee(Number(e.target.value))} />
            </div>
          )}
          {showFixed && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fixed commission amount</Label>
              <Input type="number" data-testid="calc-fixed" className="mt-1" value={fixedCommission}
                     onChange={(e) => setFixedCommission(Number(e.target.value))} />
            </div>
          )}
          {showPct && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Placement-fee percentage: {placementPct}%</Label>
              <Slider data-testid="calc-placement-pct" className="mt-3" value={[placementPct]} min={0} max={100} step={5}
                      onValueChange={(v) => setPlacementPct(v[0])} />
            </div>
          )}
          {showShare && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Agent share: {agentShare}%</Label>
              <Slider data-testid="calc-agent-share" className="mt-3" value={[agentShare]} min={0} max={100} step={5}
                      onValueChange={(v) => setAgentShare(v[0])} />
            </div>
          )}
        </div>
      </Card>

      <Card className="flex flex-col justify-between bg-navy p-6 text-white lg:col-span-2" data-testid="calc-output">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-300">Illustrative estimate</p>
          <div className="mt-6 space-y-5">
            <Row label="Estimated customer value" value={money(result.customerValue)} />
            <Row label="Provider placement / acquisition fee" value={money(result.providerReceives)} />
            <div className="rounded-lg bg-emeraldbrand/15 p-4">
              <p className="text-xs uppercase tracking-wide text-emerald-200">Potential agent commission</p>
              <p className="mt-1 font-heading text-4xl font-bold text-emerald-300" data-testid="calc-agent-commission">
                {money(result.agentCommission)}
              </p>
            </div>
            <Row label="Provider retained economics" value={money(result.providerRetained)} />
          </div>
        </div>
      </Card>

      {!compact && <div className="lg:col-span-5"><IllustrativeBanner /></div>}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-white/10 pb-3">
      <span className="text-sm text-slate-300">{label}</span>
      <span className="font-heading text-lg font-semibold">{value}</span>
    </div>
  );
}
